import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Header from '../components/Header';
import Appfooter from '../components/Appfooter';
import { getSubscriptions } from '../services/user';
import LeftMenu from '../components/LeftMenu';
import i18n from '../utils/i18n';
import { OverlayTrigger, Spinner, Tooltip } from 'react-bootstrap';
import { Modal } from 'react-bootstrap';
import { cancelSubscription } from '../services/creator';
import SubscriptionStatus from '../enums/SubscriptionStatus';
import { format } from 'date-fns';
import { trackSubscriptionCancellation } from '../utils/analytics';
import Button from '../components/Button';
import { useNavigate } from 'react-router-dom';

const Subscriptions = () => {
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [showUnsubscribeModal, setShowUnsubscribeModal] = useState(false);
  const [selectedSubscriptionId, setSelectedSubscriptionId] = useState('');
  const navigate = useNavigate();

  const loadSubscriptions = useCallback(async () => {
    try {
      const subscriptions = await getSubscriptions();

      const filteredSubscriptions = subscriptions.filter(
        (subscription) =>
          subscription.status !== SubscriptionStatus.NEW &&
          subscription.status !== SubscriptionStatus.CANCELLED
      );

      setSubscriptions(filteredSubscriptions);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSubscriptions();
  }, [loadSubscriptions]);

  const unsubscribe = async () => {
    try {
      setLoading(true);
      const subscription = subscriptions.find(
        (sub) => sub.id === selectedSubscriptionId
      );
      if (subscription) {
        trackSubscriptionCancellation(
          selectedSubscriptionId,
          subscription.creatorId
        );
      }
      await cancelSubscription(selectedSubscriptionId);
      await loadSubscriptions();
    } catch (error) {
      console.error('Erro ao cancelar assinatura:', error);
    } finally {
      setLoading(false);
    }
  };

  const selectedSubscription = useMemo(() => {
    return subscriptions.find(
      (subscription) => subscription.id === selectedSubscriptionId
    );
  }, [subscriptions, selectedSubscriptionId]);

  return (
    <>
      <Header />
      <LeftMenu />
      <div className="main-content right-chat-active">
        <div className="middle-sidebar-bottom">
          <div className="middle-sidebar-left">
            <div className="middle-wrap">
              <div className="card w-100 border-0 bg-white shadow-xs p-0 mb-4">
                {loading ? (
                  <div
                    className="align-self-center d-flex flex-column justify-content-center align-items-center"
                    style={{
                      padding: 100,
                    }}
                  >
                    <Spinner
                      animation="border"
                      role="status"
                      className="text-warning"
                    />
                  </div>
                ) : (
                  <>
                    <div className="card-body p-4 p-lg-5 w-100 border-0">
                      <div className="d-flex align-items-center justify-content-between mb-4">
                        <h4 className="font-md text-grey-900 fw-700 mb-0">
                          {i18n.t<string>('Subscriptions.title', {
                            number: subscriptions.length,
                          })}
                        </h4>
                      </div>

                      {subscriptions.length ? (
                        <div className="row">
                          <div className="col-12">
                            {subscriptions.map((subscription, index) => (
                              <div
                                key={index}
                                className="d-flex align-items-center p-3 border-bottom"
                              >
                                {/* Avatar */}
                                <div
                                  className="avatar me-2 me-md-3 cursor-pointer flex-shrink-0 d-flex"
                                  style={{
                                    width: '45px',
                                    height: '45px',
                                  }}
                                  onClick={() =>
                                    navigate(
                                      `/${subscription.metadata.username}`
                                    )
                                  }
                                >
                                  <img
                                    src={
                                      subscription.metadata.profile.avatar ||
                                      'assets/images/user-100x100.png'
                                    }
                                    alt={subscription.metadata.username}
                                    className="rounded-circle"
                                    style={{
                                      width: '100%',
                                      height: '100%',
                                      objectFit: 'cover',
                                    }}
                                  />
                                </div>

                                {/* User Info */}
                                <div
                                  className="flex-grow-1"
                                  style={{ minWidth: 0 }}
                                >
                                  <h4
                                    className="fw-700 text-grey-900 font-xsss mb-0 cursor-pointer d-flex align-items-center"
                                    onClick={() =>
                                      navigate(
                                        `/${subscription.metadata.username}`
                                      )
                                    }
                                  >
                                    <span
                                      style={{
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        marginRight: '8px',
                                      }}
                                    >
                                      {subscription.metadata.username}
                                    </span>
                                    {subscription.status ===
                                      SubscriptionStatus.ACTIVE &&
                                      subscription.cancelAt && (
                                        <OverlayTrigger
                                          placement="top"
                                          overlay={
                                            <Tooltip id="tooltip-top">
                                              Assinatura válida até{' '}
                                              {format(
                                                new Date(subscription.cancelAt),
                                                'dd/MM/yyyy'
                                              )}
                                              .
                                            </Tooltip>
                                          }
                                        >
                                          <i className="feather-info text-warning font-xs flex-shrink-0"></i>
                                        </OverlayTrigger>
                                      )}
                                  </h4>
                                  <p
                                    className="fw-500 font-xssss text-grey-500 mb-0"
                                    style={{
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    @{subscription.metadata.username}
                                  </p>
                                </div>

                                {/* Action Button */}
                                {subscription.status ===
                                  SubscriptionStatus.ACTIVE &&
                                  !subscription.cancelAt &&
                                  subscription.paymentMethod !== 'PIX' && (
                                    <button
                                      className="btn btn-sm bg-greylight text-grey-900 fw-600 rounded-xxl ms-2 flex-shrink-0"
                                      style={{
                                        minWidth: '120px',
                                        fontSize: '12px',
                                        padding: '6px 12px',
                                      }}
                                      onClick={() => {
                                        setShowUnsubscribeModal(true);
                                        setSelectedSubscriptionId(
                                          subscription.id
                                        );
                                      }}
                                    >
                                      Cancelar assinatura
                                    </button>
                                  )}
                                {subscription.status ===
                                  SubscriptionStatus.ACTIVE &&
                                  subscription.cancelAt && (
                                    <span className="badge bg-lightgrey text-grey-600 font-xssss fw-600">
                                      Expirando
                                    </span>
                                  )}
                                {subscription.status ===
                                  SubscriptionStatus.CANCELLED && (
                                  <span className="text-grey-500 font-xssss">
                                    Cancelada
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div className="align-self-center d-flex flex-column justify-content-center align-items-center p-5">
                          <img
                            src="assets/images/logo-blank.png"
                            alt="empty"
                            style={{
                              width: 50,
                              height: 50,
                              objectFit: 'contain',
                            }}
                          />
                          <span className="text-grey-600 m-2 font-xss text-center">
                            {i18n.t<string>('Subscriptions.noSubscriptions')}
                          </span>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
            <Modal show={showUnsubscribeModal}>
              <Modal.Header
                closeButton
                onClick={() => setShowUnsubscribeModal(false)}
                className="bg-white"
                style={{
                  border: 0,
                  borderRadius: 0,
                  padding: 20,
                  borderTopLeftRadius: 10,
                  WebkitBorderTopRightRadius: 10,
                }}
              />
              <Modal.Body className="bg-white">
                <h2 className="text-center fw-600">
                  {selectedSubscription?.metadata.username ?? ''}
                </h2>
                <p className="font-xs text-black text-center">
                  Deseja realmente cancelar a inscrição?
                </p>
              </Modal.Body>
              <Modal.Footer className="bg-white d-flex flex-row">
                <div className="form-group mb-1 flex-grow-1">
                  <button
                    onClick={() => setShowUnsubscribeModal(false)}
                    className="form-control text-center text-white fw-600 bg-dark border-0 p-0"
                  >
                    Não
                  </button>
                </div>
                <div className="form-group mb-1 flex-grow-1">
                  <Button
                    onClick={() => {
                      setShowUnsubscribeModal(false);
                      unsubscribe();
                    }}
                  >
                    Confirmar
                  </Button>
                </div>
              </Modal.Footer>
            </Modal>
          </div>
        </div>
      </div>
      <Appfooter />
    </>
  );
};

export default Subscriptions;
